import { useCallback } from 'react'
import { useMatiApiClient } from './client'
import { useQuery } from '@maui/react-query'
import { relevantReportsAdapter } from '@/adapters'

export const useReportsVocabApi = () => {
  const api = useMatiApiClient('actors')
  return useQuery({
    queryFn: () => api.fetchReportsVocab(),
    queryKey: ['reportsVocab'],
    refetchOnWindowFocus: false,
  })
}

export const useReportByIdQuery = (id, options = {}) => {
  const api = useMatiApiClient('report')
  const enabled = !!id

  return useQuery({
    queryFn: () => api.fetchReport(id),
    queryKey: ['report', id],
    refetchOnWindowFocus: false,
    ...options,
    enabled: (options.enabled ?? true) && enabled
  })
}

export const usePromotedReportsQuery = () => {
  const api = useMatiApiClient('report')
  return useQuery({
    queryFn: () => api.fetchPromotedReports(),
    queryKey: ['report'],
    refetchOnWindowFocus: false,
  })
}

export const fetchAdaptedData = async ({ fetchFn, fetchFnArgs, adapters }) => {
  const data = await fetchFn(...fetchFnArgs) || []
  const adaptedData = await Promise.all(adapters.map(([_, adapter]) => {
    return adapter(data)
  }))
  return adaptedData.reduce((acc, curr, i) => {
    acc[adapters[i][0]] = curr
    return acc
  }, {})
}

export const actorReportsAdapters = [
  ['relevant-reports', data => relevantReportsAdapter(data?.reports, 'Actor')],
  ['__raw', data => data]
]

export const useActorReportsQuery = (id, options = {}) => {
  if (!id) throw new Error('useActorReportsQuery requires an id')
  const api = useMatiApiClient('actors')
  const fetchFn = api.fetchActorReports
  const { adapter = false, ...restOptions } = options
  const adapterFn = actorReportsAdapters.find(([key]) => key === adapter || (adapter === false && key === '__raw'))
  const enabled = !!id

  return useQuery({
    queryFn: () => fetchAdaptedData({ fetchFn, fetchFnArgs: [id], adapters: actorReportsAdapters }),
    queryKey: ['actorsReports', adapter, id],
    refetchOnWindowFocus: false,
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
    enabled: (restOptions.enabled ?? true) && enabled,
  })
}

export const campaignsReportsAdapters = [
  ['relevant-reports', data => relevantReportsAdapter(data?.reports, 'Campaign')],
  ['__raw', data => data]
]

export const useCampaignReportsQuery = (id, options = {}) => {
  const api = useMatiApiClient('campaigns')
  const fetchFn = api.fetchCampaignReports
  const { adapter = false, ...restOptions } = options
  const adapterFn = campaignsReportsAdapters.find(([key]) => key === adapter || (adapter === false && key === '__raw'))
  const enabled = !!id

  return useQuery({
    queryFn: () => fetchAdaptedData({ fetchFn, fetchFnArgs: [id], adapters: campaignsReportsAdapters }),
    queryKey: ['campaignReports', adapter, id],
    refetchOnWindowFocus: false,
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
    enabled: (restOptions.enabled ?? true) && enabled,
  })
}

export const malwareReportsAdapters = [
  ['relevant-reports', data => relevantReportsAdapter(data?.reports, 'Malware')],
  ['__raw', data => data]
]

export const useMalwareReportsQuery = (id, options = {}) => {
  const api = useMatiApiClient('malware')
  const fetchFn = api.fetchMalwareReports
  const { adapter = false, ...restOptions } = options
  const adapterFn = malwareReportsAdapters.find(([key]) => key === adapter || (adapter === false && key === '__raw'))
  const enabled = !!id

  return useQuery({
    queryFn: () => fetchAdaptedData({ fetchFn, fetchFnArgs: [id], adapters: malwareReportsAdapters }),
    queryKey: ['malwareReports', adapter, id],
    refetchOnWindowFocus: false,
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
    enabled: (restOptions.enabled ?? true) && enabled
  })
}

export const vulnsReportsAdapters = [
  ['relevant-reports', data => relevantReportsAdapter(data?.associated_reports, 'CVE')],
  ['__raw', data => data]
]

export const useVulnsReportsQuery = (id, options = {}) => {
  if (!id) throw new Error('useVulnsReportsQuery requires an id')
  const api = useMatiApiClient('vulnerabilities')
  const fetchFn = api.getOneVulnerability
  const { adapter = false, ...restOptions } = options
  const adapterFn = vulnsReportsAdapters.find(([key]) => key === adapter || (adapter === false && key === '__raw'))
  const enabled = !!id

  return useQuery({
    queryFn: () => fetchAdaptedData({ fetchFn, fetchFnArgs: [id], adapters: vulnsReportsAdapters }),
    queryKey: ['vulnsReports', adapter, id],
    refetchOnWindowFocus: false,
    select: useCallback(data => data[adapterFn[0]] || data.__raw, [adapterFn]),
    ...restOptions,
    enabled: (restOptions.enabled ?? true) && enabled,
  })
}
